import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { relazioniAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { NotebookPen, Plus, Loader2, CalendarDays, Paperclip, ChevronRight } from 'lucide-react';

const fmtDate = (v) => v ? new Date(v).toLocaleDateString('it-IT') : null;

// Elenco relazioni di viaggio che riguardano un cliente (scheda cliente)
export const ClientRelazioni = ({ client }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const canCreate = ['admin', 'commerciale', 'area_manager'].includes(user?.role);

    useEffect(() => {
        if (!client?.id) return;
        setLoading(true);
        relazioniAPI.getAll({ clientId: client.id })
            .then(r => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [client?.id]);

    if (!client) return null;

    const openNew = () => navigate('/relazioni', { state: { prefill: {
        title: `Visita ${client.company_name}`, country: client.country || '', client_ids: [client.id],
    } } });

    return (
        <div className="space-y-2" data-testid="client-relazioni">
            <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-medium inline-flex items-center gap-1">
                    <NotebookPen size={13} /> Relazioni di viaggio {items.length > 0 && <Badge variant="outline" className="ml-1">{items.length}</Badge>}
                </span>
                {canCreate && (
                    <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={openNew} data-testid="client-relazioni-new">
                        <Plus size={12} className="mr-1" /> Nuova relazione
                    </Button>
                )}
            </div>
            {loading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Caricamento…</div>
            ) : items.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="client-relazioni-empty">Nessuna relazione per questo cliente.</p>
            ) : (
                <ul className="divide-y border rounded-sm max-h-56 overflow-y-auto">
                    {items.map(r => (
                        <li key={r.id}>
                            <button
                                type="button"
                                onClick={() => navigate('/relazioni', { state: { openId: r.id } })}
                                className="w-full text-left px-3 py-2 hover:bg-muted/40 flex items-center gap-3 transition-colors"
                                data-testid={`client-relazione-${r.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium truncate">{r.title}</div>
                                    <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3">
                                        <span>{r.country}</span>
                                        {(r.date_from || r.date_to) && <span className="inline-flex items-center gap-1"><CalendarDays size={11} /> {fmtDate(r.date_from) || '…'} → {fmtDate(r.date_to) || '…'}</span>}
                                        <span>{r.author_name}</span>
                                        {r.attachments_count > 0 && <span className="inline-flex items-center gap-1"><Paperclip size={11} /> {r.attachments_count}</span>}
                                    </div>
                                </div>
                                <ChevronRight size={14} className="text-muted-foreground shrink-0" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default ClientRelazioni;
